import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';

const CartContext = createContext(null);

const CART_STORAGE_KEY = 'liantiao_cart';

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Check if user is logged in
  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsLoggedIn(!!token);
  }, []);

  // Load cart on mount - from API if logged in, otherwise localStorage
  useEffect(() => {
    const loadCart = async () => {
      const token = localStorage.getItem('token');
      
      if (token) {
        // Try to load from backend
        try {
          const response = await api.get('/cart/');
          if (response.data && response.data.items) {
            const backendItems = response.data.items.map(item => ({
              id: item.product_id,
              title: item.product_title || item.title,
              price: item.current_price || item.unit_price,
              image: item.product_image || item.image,
              quantity: item.quantity,
              seller_name: item.seller_name || 'Liantiao Marketplace'
            }));
            setCartItems(backendItems);
            // Also update localStorage
            localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(backendItems));
            return;
          }
        } catch (error) {
          console.error('Error loading cart from backend:', error);
        }
      }
      
      // Fallback to localStorage
      try {
        const savedCart = localStorage.getItem(CART_STORAGE_KEY);
        if (savedCart) {
          setCartItems(JSON.parse(savedCart));
        }
      } catch (error) {
        console.error('Error loading cart from localStorage:', error);
      }
    };
    
    loadCart();
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartItems));
    } catch (error) {
      console.error('Error saving cart:', error);
    }
  }, [cartItems]);

  // Sync item to backend
  const syncToBackend = async (productId, quantity) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    try {
      if (quantity > 0) {
        await api.post('/cart/add', { product_id: productId, quantity });
      } else {
        await api.post('/cart/remove', null, { params: { product_id: productId } });
      }
    } catch (error) {
      console.error('Error syncing cart to backend:', error);
    }
  };

  const addToCart = useCallback(async (product, quantity = 1) => {
    setCartItems(prevItems => {
      const existingItem = prevItems.find(item => item.id === product.id);
      
      if (existingItem) {
        const newItems = prevItems.map(item =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
        toast.success(`Updated ${product.title || product.name} quantity in cart`);
        return newItems;
      } else {
        const newItem = {
          id: product.id,
          title: product.title || product.name,
          price: product.price,
          compare_at_price: product.compare_at_price,
          image: product.images?.[0] || product.image || '',
          seller_name: product.seller_name || 'Liantiao Marketplace',
          quantity
        };
        toast.success(`Added ${product.title || product.name} to cart`);
        return [...prevItems, newItem];
      }
    });
    
    // Sync to backend
    await syncToBackend(product.id, quantity);
  }, []);

  const removeFromCart = useCallback(async (productId) => {
    setCartItems(prevItems => {
      const item = prevItems.find(i => i.id === productId);
      if (item) {
        toast.info(`Removed ${item.title} from cart`);
      }
      return prevItems.filter(item => item.id !== productId);
    });
    
    // Sync to backend
    await syncToBackend(productId, 0);
  }, []);

  const updateQuantity = useCallback(async (productId, quantity) => {
    if (quantity < 1) {
      removeFromCart(productId);
      return;
    }
    
    setCartItems(prevItems =>
      prevItems.map(item =>
        item.id === productId ? { ...item, quantity } : item
      )
    );
    
    // Sync to backend - for update we need to set the exact quantity
    const token = localStorage.getItem('token');
    if (token) {
      try {
        await api.post('/cart/update', { product_id: productId, quantity });
      } catch (error) {
        console.error('Error updating cart:', error);
      }
    }
  }, [removeFromCart]);

  const clearCart = useCallback(async () => {
    setCartItems([]);
    toast.info('Cart cleared');
    
    // Clear backend cart
    const token = localStorage.getItem('token');
    if (token) {
      try {
        await api.delete('/cart/clear');
      } catch (error) {
        console.error('Error clearing backend cart:', error);
      }
    }
  }, []);

  const getCartTotal = useCallback(() => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  }, [cartItems]);

  const getCartCount = useCallback(() => {
    return cartItems.reduce((count, item) => count + item.quantity, 0);
  }, [cartItems]);

  const isInCart = useCallback((productId) => {
    return cartItems.some(item => item.id === productId);
  }, [cartItems]);

  // Sync localStorage cart to backend when user logs in
  const syncCartOnLogin = useCallback(async () => {
    const savedCart = localStorage.getItem(CART_STORAGE_KEY);
    if (!savedCart) return;
    
    try {
      const items = JSON.parse(savedCart);
      for (const item of items) {
        await api.post('/cart/add', { product_id: item.id, quantity: item.quantity });
      }
      // Reload cart from backend to get enriched data
      const response = await api.get('/cart/');
      if (response.data && response.data.items) {
        const backendItems = response.data.items.map(item => ({
          id: item.product_id,
          title: item.product_title || item.title,
          price: item.current_price || item.unit_price,
          image: item.product_image || item.image,
          quantity: item.quantity,
          seller_name: item.seller_name || 'Liantiao Marketplace'
        }));
        setCartItems(backendItems);
      }
    } catch (error) {
      console.error('Error syncing cart on login:', error);
    }
  }, []);

  const value = {
    cartItems,
    cartCount: getCartCount(),
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartCount,
    isInCart,
    isCartOpen,
    setIsCartOpen,
    syncCartOnLogin
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export default CartContext;
